import {
  cardClassName,
  chartColors,
  panelSubtleClassName,
  panelTitleClassName,
} from "@/modules/utils/dashboard/constants";
import {
  AiDistributionPoint,
  JobTypePerformancePoint,
  RadarPoint,
} from "@/modules/types/dashboard";
import {
  dashboardTooltipStyle,
  formatDashboardNumber,
} from "@/modules/utils/dashboard/formatter";
import { useClientTranslations } from "@/lib/translate";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type DashboardDeepInsightsSectionProps = {
  aiMatchingDistribution: AiDistributionPoint[];
  potentialZoneCount: number;
  radarData: RadarPoint[];
  jobTypePerformanceData: JobTypePerformancePoint[];
};

export const DashboardDeepInsightsSection = ({
  aiMatchingDistribution,
  potentialZoneCount,
  radarData,
  jobTypePerformanceData,
}: DashboardDeepInsightsSectionProps) => {
  const { t } = useClientTranslations("dashboard-page");

  return (
    <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <article className={cardClassName}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className={panelTitleClassName}>{t("dashboard.deepInsights.aiMatchingTitle")}</h2>
          <span className={panelSubtleClassName}>
            {t("dashboard.deepInsights.potentialZone")}:{" "}
            {formatDashboardNumber(potentialZoneCount)}
          </span>
        </div>
        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={aiMatchingDistribution}>
              <defs>
                <linearGradient id="aiAreaColor" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartColors.primary} stopOpacity={0.4} />
                  <stop offset="95%" stopColor={chartColors.primary} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(148 163 184 / 0.35)" />
              <XAxis dataKey="score" tick={{ fontSize: 12, fill: "var(--foreground)" }} />
              <YAxis tick={{ fontSize: 12, fill: "var(--foreground)" }} />
              <Tooltip
                formatter={(value) => {
                  const numericValue = Array.isArray(value)
                    ? Number(value[0] ?? 0)
                    : Number(value ?? 0);
                  return formatDashboardNumber(
                    Number.isFinite(numericValue) ? numericValue : 0,
                  );
                }}
                labelFormatter={(label) =>
                  `${t("dashboard.deepInsights.aiScoreLabel")}: ${label}`
                }
                contentStyle={dashboardTooltipStyle}
              />
              <ReferenceArea x1={80} x2={100} fill={chartColors.success} fillOpacity={0.12} />
              <Area
                type="monotone"
                dataKey="count"
                stroke={chartColors.primary}
                strokeWidth={2}
                fill="url(#aiAreaColor)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </article>

      <article className={cardClassName}>
        <h2 className={`mb-3 ${panelTitleClassName}`}>
          {t("dashboard.deepInsights.employerBrandRadarTitle")}
        </h2>
        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData}>
              <PolarGrid stroke="rgb(148 163 184 / 0.45)" />
              <PolarAngleAxis
                dataKey="subject"
                tick={{ fontSize: 11, fill: "var(--foreground)" }}
                tickFormatter={(value) =>
                  t(`dashboard.deepInsights.radar.${String(value)}`)
                }
              />
              <Tooltip
                formatter={(value) => {
                  const numericValue = Array.isArray(value)
                    ? Number(value[0] ?? 0)
                    : Number(value ?? 0);
                  return (Number.isFinite(numericValue) ? numericValue : 0).toFixed(2);
                }}
                contentStyle={dashboardTooltipStyle}
              />
              <Radar
                dataKey="value"
                stroke={chartColors.secondary}
                fill={chartColors.secondary}
                fillOpacity={0.35}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </article>

      <article className={cardClassName}>
        <h2 className={`mb-3 ${panelTitleClassName}`}>
          {t("dashboard.deepInsights.jobTypePerformanceTitle")}
        </h2>
        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={jobTypePerformanceData} barGap={8}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(148 163 184 / 0.35)" />
              <XAxis dataKey="group" tick={{ fontSize: 11, fill: "var(--foreground)" }} />
              <YAxis tick={{ fontSize: 12, fill: "var(--foreground)" }} />
              <Tooltip
                formatter={(value) => {
                  const numericValue = Array.isArray(value)
                    ? Number(value[0] ?? 0)
                    : Number(value ?? 0);
                  return formatDashboardNumber(
                    Number.isFinite(numericValue) ? numericValue : 0,
                  );
                }}
                contentStyle={dashboardTooltipStyle}
              />
              <Legend />
              <Bar
                dataKey="hot"
                name={t("dashboard.deepInsights.jobType.hot")}
                fill={chartColors.hot}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="special"
                name={t("dashboard.deepInsights.jobType.special")}
                fill={chartColors.secondary}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="normal"
                name={t("dashboard.deepInsights.jobType.normal")}
                fill={chartColors.primary}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </article>
    </section>
  );
};
